import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/our-travel-wishlist-for-2022`;

  return (
    <MainLayout>
      <NextSeo
        title="Our Travel Wishlist for 2022 | Win Epic Prizes | DAYMADE"
        description="Our top 10 wishlisted travel prizes show you where to go in 2022! From trending sun-kissed islands to wintery snowscapes, here’s our list of Insta worthy destinations."
        openGraph={{
          title: 'Our Travel Wishlist for 2022 | Win Epic Prizes | DAYMADE',
          description:
            'Our top 10 wishlisted travel prizes show you where to go in 2022! From trending sun-kissed islands to wintery snowscapes, here’s our list of Insta worthy destinations.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/our-travel-wishlist-for-2022',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Woman relaxing in a hammock swing on beach overlooking blue water',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'where to go in 2022,bucketlist,wishlist,holiday,destinations,Maldives,Iceland,bora bora,new Zealand,Japan,Barbados,Mexico,Philippines,California,bali,win a holiday,win a prize',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Woman relaxing in a hammock swing on beach overlooking blue water"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            Our Travel Wishlist for <strong>2022</strong>
          </h1>

          <p className="subheading">DAYMADE’s top 10 travel destinations</p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            Want to know where to go in 2022? We’ve compiled a list of our
            players’ most wishlisted travel destinations and DAYMADE holidays.
            Will you win a trip to Japan or Bali? Read on to find out where your
            2022 might take you…
          </p>

          <h4>The Maldives</h4>

          <p>
            We’re not surprised to find The Maldives at the top of our list of
            most-wishlisted travel destinations. With unrivalled luxury and
            breath-taking scenery, Maldives has become the ultimate
            once-in-a-lifetime holiday destination. If you’re after an
            all-inclusive, luxurious retreat, our most-loved prize holiday to
            The Maldives has got you covered. Maldives spans nearly 1,200
            islands, with white powdery sand and cyan-blue waters at every turn.
            Dazzling coral and colourful marine life offers impeccable diving
            experiences, with the chance of seeing whale sharks and turtles too
            great an opportunity to miss. Heading away from the stunning
            Maldivian sea and turning inland, you’ll find tropical islands rich
            in culture. Try your hand at cooking the local cuisine or giving
            back by coral planting, before relaxing on your resort’s private
            island with brilliant-blue waters. Bucketlist? Tick.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1-the-maldives.jpg`}
            alt="Woman in bungalow villa and infinity pool overlooking the Maldives"
            className="singleBlogImage"
          />

          <h4>Iceland</h4>

          <p>
            The Northern Lights alone are enough to warrant second place on our
            wishlist, let alone everything else Iceland has to offer! With vast
            volcanoes, gushing geysers and glimmering glaciers, this sprawling
            land is a true symphony of elements. The power of nature is evident
            nowhere else more so than in Iceland, where geothermal lagoons and
            glooping mudpots bewilder even the most experienced traveller. Chase
            the aurora borealis across the night sky by foot or by husky-sled.
            In this land, the world really is your oyster. Live music and visual
            arts are celebrated across the land, allowing you to immerse
            yourself in Icelandic culture first-hand.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2-iceland.jpg`}
            alt="Aurora borealis blue curtain over volcanoes of Iceland"
            className="singleBlogImage"
          />

          <h4>Bora Bora</h4>

          <p>
            Bora Bora is a visual spectacle, with white sandy beaches, crystal
            clear waters and tropical forests just waiting to be explored.
            Sprinkled with overwater bungalows and lagoon-side villas, there’s a
            luxurious air exuding from French Polynesia’s most famous island.
            Quite possibly the ultimate honeymoon destination, Bora Bora is
            studded with gems set to take your breath away. Regardless of your
            holiday-persona there’s something for everyone, from private
            tastings of local delicacies to guided tours of historic landmarks.
            Try your hand at an off-road adventure, or discover underwater reefs
            with an eco-snorkel cruise, seeing sharks and stingrays up close.
            However you decide to spend your holiday in Bora Bora, you’ll be
            surrounded by beautiful landscapes and stunning wildlife.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3-bora-bora.jpg`}
            alt="Bungalow villas over white sandy beach and blue waters of Bora Bora"
            className="singleBlogImage"
          />

          <h4>New Zealand</h4>

          <p>
            New Zealand is on every traveller’s bucketlist and it’s easy to see
            why with its dynamic Māori culture and mammoth national parks.
            Sublime forests, lakes, beaches and mountains cover the island’s
            wilderness, with stunning vistas stretching as far as the eye can
            see. Water sports come into their own here, with kayaking, surfing
            and scuba diving opportunities around every corner of pristine
            coastline. To explore as much of this island as possible, we’ve got
            a 2-week roadtrip filled with as many unique activities as you can
            imagine. Think visiting the glowworm caves in Lake Te Anau, surfing
            in Manu Bay, or canoeing in Abel Tasman National Park. Now we
            couldn’t send you all the way to New Zealand without giving you a
            set tour of Hobbiton now could we? Each day is filled with
            instagrammable moments, whether you’re a nature-nurturer or an
            adrenaline-junkie. Oh and there’s up to £3,000 spending money to
            enjoy on your holiday to New Zealand just to sweeten the deal!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4-new-zealand.jpg`}
            alt="Roadtrip past Lake Pukaki and mountains of New Zealand"
            className="singleBlogImage"
          />

          <h4>Japan</h4>

          <p>
            If travelling for you is all about stepping into a whole new world,
            there’s nowhere better to visit than Japan. With cherry blossom
            gardens, temples and sushi-galore, the land of the rising sun has so
            much to explore. We’ve thrown in a whole host of activities, from
            adventuring through Shiralto Falls before gazing upon{' '}
            <a
              target="_blank"
              href="https://www.pretraveller.com/best-mt-fuji-one-day-tour-from-tokyo/"
              rel="noopener noreferrer"
            >
              Mount Fuji
            </a>{' '}
            to exploring Matsumoto Castle. An unlimited rail card will see you
            travelling across Japan’s prefectures so you can travel stress-free,
            whooshing from Tokyo to Osaka and beyond. Take the opportunity to
            connect with Japan’s traditional culture by staying in ryokans,
            visiting onsen and learning how to meticulously prepare delicate
            dishes celebrating Japanese cuisine. Exploring this diverse land is
            at the top of many of our team’s bucketlists, so why not come join
            us on our next adventure?
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5-japan.jpg`}
            alt="Friends crossing neon-lit Shibuya crossing in Tokyo, Japan"
            className="singleBlogImage"
          />

          <h4>Barbados</h4>

          <p>
            Now we might be biased here, seeing as our co-founder is Barbadian,
            but Barbados really does have it all. Famous for its fantastic
            beaches and turquoise-blue bays, this Gem of the Caribbean Sea has a
            huge array to offer, whether you’re an adventure-seeker or laid-back
            beach-bum. Buckle up for a 4x4 safari tour to explore the island’s
            forests and remote bays. More of a water-babe? Dive right in and
            swim with turtles, snorkel over shipwrecks and admire the vibrant
            marine life. The Unesco World Heritage-listed capital of Bridgetown
            offers architectural wonders to walk through, from St. Nicholas to
            Chamberlain Bridge. This lush paradise of an island is as welcoming
            as it is beautiful, so it’s no wonder it’s on many peoples’ travel
            wishlists.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6-barbados.jpg`}
            alt="View of palm trees and rock cliffs over white sandy beach and blue waters of Barbados"
            className="singleBlogImage"
          />

          <h4>Mexico</h4>

          <p>
            A diverse landscape like no other, Mexico is filled with vibrant
            jungles, snowy volcanoes and cactus-strewn deserts, not to mention
            endless beaches and wildlife-filled lagoons. With a chili-fied
            cuisine beloved the world over, Mexico is a delight for all the
            senses. Ziplining through jungle canopies? They’ve got it. Ancient
            Mayan ruins? They’re everywhere! This is one action-packed holiday
            prize just waiting to help you tick off your bucketlist. We suggest
            taking a day to slow down, explore the Riviera Maya and Mesoamerican
            Barrier Reef, and enjoy what can only be described as paradise. With
            a once in a lifetime holiday, it’s worth it to savour every moment.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary7-mexico.jpg`}
            alt="Jagged cliffs in blue waters off the coast of Mexico"
            className="singleBlogImage"
          />

          <h4>Philippines</h4>

          <p>7,000 tropical islands. What more is there to say?</p>

          <p>
            In all seriousness, the Philippines brings something unique to our
            top 10. With an island to suit every taste (literally), it’s firmly
            earned its spot on our wishlist. Beach bums will find marooned sandy
            beaches and incredible island-hopping opportunities on the hunt for
            their perfect beach. More adventurous travellers, meanwhile, can
            explore the volcanic lagoons and world-class kiteboarding in North
            Luzon. Having a rare mix of Southeast Asian, Spanish and American
            influences, the Philippines is a land with its own fiercely unique
            personality. The people, however, are relentlessly welcoming, warm
            and guaranteed to be one of the reasons you’ll find yourself coming
            back time and time again.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary8-the-philippines.jpg`}
            alt="Woman relaxing in jungle waterfall in the Philippines"
            className="singleBlogImage"
          />

          <h4>California</h4>

          <p>
            With tales told by misty redwood forests and neon city lights,
            California is a land of captivating contrasts. The best way to see
            it all is via the classic road trip, crossing the iconic Route 66 in
            a Volkswagen van. Choose to explore the neon signs and casinos of
            Las Vegas, see the famous Hollywood sign up close, and discover the
            mysterious island of Alcatraz. Or if you prefer to get reacquainted
            with nature, there’s national parks galore with Yosemite, Joshua
            Tree and Sequoia just waiting to impress even the most jaded
            traveller. Our £25,000 prize holiday to California also includes
            business flights, a Vegas Residency show and of course tickets to
            the likes of Disneyland and Universal Studios to name a few. This
            California road trip is sure to leave a lasting impression and help
            you tick off your ever-growing bucketlist.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary9-california.jpg`}
            alt="Friends on a California roadtrip running from Volkswagen van to sandy beach"
            className="singleBlogImage"
          />

          <h4>Bali</h4>

          <p>
            The mere mention of Bali evokes thoughts of mediation, relaxation
            and rewinding. After everything the last two years has brought, no
            wonder it’s in our top ten most wishlisted destinations! An escape
            for the workaholics and famous for its abundance of yoga retreats,
            Bali offers more than just a picturesque landscape. The rich and
            diverse culture is visible at all levels of life, allowing you the
            opportunity to experience it through traditional music and dance
            festivities, with flower-petal offerings scattered throughout the
            island. Dramatic volcanoes, wild beaches and sprawling rice fields
            are contrasted against a myriad of ancient temples and monuments.
            Bali offers the chance to take a step back, live in the moment and
            experience a new way of life. No wonder our holiday to Bali has
            peoples’ hearts swooning.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary10-bali.jpg`}
            alt="Couple overlooking Bali forest and rice fields"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p style={{ textAlign: 'center' }}>
            Want to win your ultimate holiday? Enter the DAYMADE weekly draw for
            your chance to win a holiday for just £3!
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
